import { ConnectionDetails, convertConnectionToDetails, handleError, mergeConnectionDetails, queryInput } from '@/utils/custom';
import { DtrLocation, DtrLocationConnection, DtrLocationConnectionInput, DtrLocationSearch } from '@/models/gen/graphql';

import { GraphApiResponse } from '@/api/core';
import { NormalizedLocation } from '@/api/services/locations/getNormalizedAddress';
import { SearchDtrLocationDocument } from '@/api/queries';
import createGraphApiHook from '@/hooks/createGraphApiHook';
import graphApi from '@/api';

export type SearchDtrLocationGraphApiResponse = GraphApiResponse<typeof SearchDtrLocationDocument>;
export type SearchDtrLocationOptions = { pageSize?: number; page?: number; merge?: true };

export const SEARCH_DTR_LOCATION_PAGE_SIZE = 1000;
const title = 'Search Locations';

export const searchDtrLocationResponseSelector = (res: SearchDtrLocationGraphApiResponse): ConnectionDetails<DtrLocation> =>
  convertConnectionToDetails(res?.searchDtrLocation?.dtrLocationConnection as DtrLocationConnection);

const [, runRefetchSearchDtrLocation] = graphApi(SearchDtrLocationDocument, {
  onError: (res: SearchDtrLocationGraphApiResponse): void => handleError(res, { notification: { title } }),
});

const searchDtrLocation = async (
  query?: DtrLocationSearch[],
  options: SearchDtrLocationOptions = {}
): Promise<ConnectionDetails<DtrLocation>> => {
  const { pageSize = SEARCH_DTR_LOCATION_PAGE_SIZE, page = 0, merge = false } = options;

  const input: DtrLocationConnectionInput = {
    first: pageSize || null,
    after: pageSize ? (pageSize * page).toString() : null,
    query,
  };
  const graphApiOptions = { merge: merge ? 'searchDtrLocation.dtrLocationConnection' : undefined };

  const res = await runRefetchSearchDtrLocation({ input }, graphApiOptions);

  return searchDtrLocationResponseSelector(res);
};

export const getDtrLocation = async (input: NormalizedLocation, airportCode?: string): Promise<DtrLocation | undefined> => {
  const res = await searchDtrLocation([
    {
      address: queryInput(input.address),
      cityName: queryInput(input.cityName),
      stateCode: queryInput(input.stateCode),
      zipCode: queryInput(input.zipCode),
      airportCode: queryInput(airportCode), // limit search by airportCode when provided
    },
  ]);
  return res?.rows?.[0];
};

export const useSearchDtrLocation = createGraphApiHook(searchDtrLocation, {
  merge: mergeConnectionDetails,
});

export default searchDtrLocation;
